import { useMemo } from 'react';

import { BandStrip } from './BandStrip.js';
import { LayerPanel } from '../maps/LayerPanel.js';
import { OffMapStrip } from '../maps/OffMapStrip.js';
import { OrbitalMap } from '../maps/OrbitalMap.js';
import type { MapContextKind, MapModifierClick } from '../maps/RegionMap.js';
import {
  applyPick,
  naturalSlotFor,
  pickRowsFor,
  targetForSubject,
  type PickRow,
} from '../model/builder.js';
import { armedCount, filterRoster } from '../model/filter.js';
import { SPACE_LAYER_LABELS } from '../model/map-layers.js';
import { paramSpecOf } from '../model/missions.js';
import { bandRows, crossLinkFor } from '../model/orbital.js';
import { toDrafts } from '../model/queue.js';
import { rosterOf } from '../model/roster.js';
import type { ScenarioModel } from '../model/scenario.js';
import { offMapNoticeOf, resolveSelection } from '../model/selection.js';
import { useConsoleStore } from '../state/store.js';
import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * The orbital board — the design's `taskableView` for `SPACE · SDA` (349–351).
 *
 * Same pane furniture as the theatre map, because the design has one: a canvas
 * region with three mutually exclusive `x-import`s in it and one LAYERS control
 * above them (markup 343–370). So `maps/LayerPanel.tsx` is **lifted** and the
 * pane's own frame is **forked**, which is the M5-D3 answer this phase owed:
 * the panel is 150 lines of control with two callers and one difference between
 * them, and the frame is a `<div>` of six layout properties whose second caller
 * has a different set of children under it. Extracting the second would have
 * produced a component whose whole body was a `style` object.
 *
 * ## Every gesture ends where the same gesture from the rail ends
 *
 * A marker click and a roster row click both call `select`. ⇧-click fills the
 * slot `naturalSlotFor` picks. A right-click anywhere opens the menu the shell
 * owns, on the same subject. None of that is re-decided here — this file routes
 * and `model/` decides, which is what stops the two boards drifting apart about
 * what a click means.
 *
 * The one gesture the theatre has and this board does not is the drag: there is
 * no `bs-engage` on `<orbital-map>`, so an SDA seat reaches the builder through
 * the context menu, the modifier clicks and the pick slots.
 *
 * ## The strip under the canvas is not decoration
 *
 * `sda/BandStrip.tsx` holds everything the wire places in a band without placing
 * it in a **slot** — which in turn 1 is every hostile in space — plus the two
 * numbers `orbital-map.js` refuses to draw on its own rings. `model/orbital.ts`
 * is the argument.
 */

export const SDA_MIN_HEIGHT = 420;

export interface SdaViewProps {
  readonly view: CellViewWire;
  readonly model: ScenarioModel;
  /** The `unit` / `band` / `canvas` menus, which the shell owns and routes. */
  readonly onContextMenu: (
    subject: 'unit' | 'band' | 'canvas',
    id: string,
    x: number,
    y: number,
  ) => void;
}

const CONTEXT_SUBJECT: Readonly<Record<MapContextKind, 'unit' | 'band' | 'canvas'>> = Object.freeze(
  // `region` on this canvas is a **ring**, and the id it carries is a band —
  // `orbital-map.js` reuses the event and not the vocabulary (its `selectRegime`
  // emits `bs-region` with `'GEO'`). Translated once, here.
  { unit: 'unit', region: 'band', empty: 'canvas' },
);

export function SdaView(props: SdaViewProps): JSX.Element {
  const view = props.view;

  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const layers = useConsoleStore((state) => state.spaceLayers);
  const toggleSpaceLayer = useConsoleStore((state) => state.toggleSpaceLayer);
  const panelOpen = useConsoleStore((state) => state.mapLayerPanelOpen);
  const setPanelOpen = useConsoleStore((state) => state.setMapLayerPanelOpen);
  const hoverId = useConsoleStore((state) => state.hoverId);
  const setHover = useConsoleStore((state) => state.setHover);
  const command = useConsoleStore((state) => state.mapCommand);
  const clearMapCommand = useConsoleStore((state) => state.clearMapCommand);

  const search = useConsoleStore((state) => state.search);
  const facets = useConsoleStore((state) => state.facets);
  const queue = useConsoleStore((state) => state.queue);
  const builder = useConsoleStore((state) => state.builder);
  const armedSlot = useConsoleStore((state) => state.armedSlot);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillMission = useConsoleStore((state) => state.fillMission);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const fillParam = useConsoleStore((state) => state.fillParam);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const showToast = useConsoleStore((state) => state.showToast);

  /** What the roster filter matched, or `null` — the left rail's two inputs, once. */
  const dim = useMemo<readonly string[] | null>(() => {
    if (search === '' && armedCount(facets) === 0) return null;
    const roster = rosterOf(view, toDrafts(queue));
    return filterRoster(roster, search, facets).flatMap((group) => group.rows.map((row) => row.id));
  }, [view, queue, search, facets]);

  const pickRows = useMemo<readonly PickRow[]>(
    () =>
      armedSlot === 'asset' || armedSlot === 'target' ? pickRowsFor(view, builder, armedSlot) : [],
    [view, builder, armedSlot],
  );
  const pick = useMemo(
    () =>
      armedSlot !== null && pickRows.length > 0
        ? { slot: armedSlot, ids: pickRows.map((row) => row.id) }
        : null,
    [armedSlot, pickRows],
  );

  /**
   * The selected unit, for the two surfaces that both have to show it.
   *
   * The rings mark a selected marker and the strip now marks a selected row, and
   * they are fed the same value rather than each deciding: a contact is on one
   * surface or the other, never both, so one expression covers the board.
   */
  const selectedUnit =
    selection !== null && (selection.kind === 'ASSET' || selection.kind === 'CONTACT')
      ? selection.id
      : null;

  const strip = useMemo(() => bandRows(view), [view]);
  const crossLink = useMemo(() => crossLinkFor(view, selection), [view, selection]);

  /**
   * The dead end this board had, pointing the other way from the theatre's.
   *
   * Five of the twelve contacts a BLUFOR seat holds in turn 1 are on the ground,
   * and selecting one here filled the chip and filled the rail and left the board
   * byte-identical — the same *did I mis-click / is it broken* the theatre strip
   * was built to answer. `offMapNoticeOf` counts the band strip as part of this
   * board, so a transiting asset and a band-only contact are present and get no
   * notice; only ground answers with `→ REGION MAP`.
   */
  const offMap = useMemo(
    () => offMapNoticeOf(resolveSelection(view, selection), 'SDA'),
    [view, selection],
  );

  const takeRow = (row: PickRow): void => {
    applyPick(row, {
      asset: (id) => fillSlot('asset', id),
      mission: fillMission,
      target: fillTarget,
      param: (value) => {
        const spec = builder.mission === null ? null : paramSpecOf(builder.mission);
        if (spec !== null) fillParam(spec.key, value);
      },
    });
  };

  /** ⇧ fills the natural slot, ⌥ forces the target slot, ⌘ collects into the batch. */
  const onModClick = (detail: MapModifierClick): void => {
    if (detail.meta) {
      toggleBatch(detail.id);
      return;
    }
    const slot = detail.alt ? 'target' : detail.shift ? naturalSlotFor(view, detail.id) : null;
    if (slot === null) return;
    if (slot === 'asset') {
      fillSlot('asset', detail.id);
      showToast('ASSET ← ' + detail.id);
      return;
    }
    const target = targetForSubject(view, detail.id);
    if (target === null) {
      showToast(detail.id + ' CANNOT BE A TARGET', 'warn');
      return;
    }
    fillTarget(target);
    showToast('TARGET ← ' + detail.id);
  };

  return (
    <div
      data-testid="sda-view"
      style={{
        position: 'relative',
        // `1 1 auto` with a floor, for the reason `MwView` states — and which
        // Phase 9 restated when it retired the harness this originally guarded
        // against: the floor is what keeps a short viewport from collapsing the
        // board, and the pane's column scrolls instead.
        flex: '1 1 auto',
        minHeight: SDA_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--canvas)',
      }}
    >
      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
        <OrbitalMap
          model={props.model}
          layers={layers}
          selectedUnit={selectedUnit}
          dim={dim}
          pick={pick}
          hover={hoverId}
          crossLink={crossLink}
          command={command}
          onSelect={(id) => select(id === null ? null : subjectOf(view, id))}
          onBand={(band) => {
            if (band !== null) select({ kind: 'BAND', id: band });
          }}
          onPick={(slot, id) => {
            const row = pickRows.find(
              (candidate) => candidate.slot === slot && candidate.id === id,
            );
            if (row !== undefined) takeRow(row);
          }}
          onContext={(kind, id, x, y) => props.onContextMenu(CONTEXT_SUBJECT[kind], id, x, y)}
          onHover={setHover}
          onModClick={onModClick}
          onCommandApplied={clearMapCommand}
        />
        <LayerPanel
          labels={SPACE_LAYER_LABELS}
          layers={layers}
          open={panelOpen}
          onToggleOpen={() => setPanelOpen(!panelOpen)}
          onToggle={toggleSpaceLayer}
        />
        {offMap !== null && <OffMapStrip notice={offMap} testId="sda-off-map" />}
      </div>
      <BandStrip
        rows={strip}
        hostile={layers.hostile}
        selected={selection?.kind === 'BAND' ? selection.id : null}
        selectedId={selectedUnit}
        onSelectBand={(band) => select({ kind: 'BAND', id: band })}
        onSelect={select}
        onBandContextMenu={(id, x, y) => props.onContextMenu('band', id, x, y)}
        onUnitContextMenu={(id, x, y) => props.onContextMenu('unit', id, x, y)}
      />
    </div>
  );
}

/** A map click names an id; the view says whether that is an asset or a contact. */
function subjectOf(
  view: CellViewWire,
  id: string,
): { readonly kind: 'ASSET' | 'CONTACT'; readonly id: string } | null {
  if (view.contacts.some((contact) => contact.id === id)) return { kind: 'CONTACT', id };
  if (view.friendly.some((asset) => asset.id === id)) return { kind: 'ASSET', id };
  return null;
}
